import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import api from "./lib/api";
import { getRouteFromNotificationData } from "./lib/notificationRouting";

const firebaseConfig = {
  apiKey: "AIzaSyBjSbM5LlWvKi2OsLCTFJagleRcJf8DFBs",
  authDomain: "qureo-e7501.firebaseapp.com",
  projectId: "qureo-e7501",
  storageBucket: "qureo-e7501.firebasestorage.app",
  messagingSenderId: "288102643376",
  appId: "1:288102643376:web:27988109e3539d9f37ad75",
  measurementId: "G-6459MRQLVL"
};

const requiredFirebaseFields = [
  "apiKey",
  "authDomain",
  "projectId",
  "appId",
  "messagingSenderId",
];

export function getFirebaseConfigIssues() {
  const issues = [];

  for (const field of requiredFirebaseFields) {
    const value = firebaseConfig[field];
    if (!value || typeof value !== "string" || !value.trim()) {
      issues.push(`Missing firebaseConfig.${field}`);
    }
  }

  if (firebaseConfig.authDomain && !firebaseConfig.authDomain.includes("firebaseapp.com")) {
    issues.push("firebaseConfig.authDomain looks invalid (expected *.firebaseapp.com)");
  }

  if (firebaseConfig.projectId && firebaseConfig.authDomain && !firebaseConfig.authDomain.startsWith(firebaseConfig.projectId)) {
    issues.push("firebaseConfig.projectId does not match authDomain prefix");
  }

  return issues;
}

export function validateFirebaseWebConfig() {
  const issues = getFirebaseConfigIssues();

  if (!issues.length) {
    return { ok: true, issues: [] };
  }

  console.error("[Firebase Config Validation] Invalid Firebase web config:", {
    issues,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
  });

  return { ok: false, issues };
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
// (auth removed)

// Messaging instance guarded by isSupported
let messaging = null;
isSupported().then((supported) => {
  if (supported) {
    messaging = getMessaging(app);
  } else {
    console.warn("Firebase Messaging is not supported in this environment.");
  }
});

validateFirebaseWebConfig();

let nativePushListenersAttached = false;

// Stores a tap-action route captured before React has mounted so NativeAppBridge
// can drain it on first render and navigate the user to the right screen.
let _pendingPushRoute = null;

const dispatchNativePushRoute = (data = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const route = getRouteFromNotificationData(data);
  // Always store in case NativeAppBridge event listener isn't attached yet (cold start)
  _pendingPushRoute = { route, data };
  window.dispatchEvent(
    new CustomEvent("qureo:nativePushAction", {
      detail: { route, data },
    })
  );
};

// Returns and clears the pending route captured before React mounted.
export const consumePendingPushRoute = () => {
  const pending = _pendingPushRoute;
  _pendingPushRoute = null;
  return pending;
};

// Register the tap listener immediately at module load time on native platforms.
// This ensures pushNotificationActionPerformed is never missed on cold start,
// regardless of when requestForToken / attachNativePushListeners is called.
if (Capacitor.isNativePlatform()) {
  try {
    PushNotifications.addListener("pushNotificationActionPerformed", (notification) => {
      dispatchNativePushRoute(notification?.notification?.data || {});
    });
  } catch (e) {
    console.warn("[Push] Early pushNotificationActionPerformed registration failed:", e);
  }
}

const attachNativePushListeners = (userId) => {
  if (nativePushListenersAttached) {
    return;
  }

  PushNotifications.addListener("registration", async (token) => {
    try {
      await api.post("/api/notifications/save-token", {
        token: token.value,
        userId,
        platform: Capacitor.getPlatform(),
      });
      console.log("Native push token successfully sent to backend");
    } catch (err) {
      console.error("Failed to save native push token:", err);
    }
  });

  PushNotifications.addListener("registrationError", (error) => {
    console.error("Native push registration error:", error);
  });

  // pushNotificationActionPerformed is registered at module load above — don't add again.

  nativePushListenersAttached = true;
};

const requestNativePushToken = async (userId) => {
  try {
    if (Capacitor.getPlatform() === "android") {
      await PushNotifications.createChannel({
        id: "qureo-alerts",
        name: "Qureo Alerts",
        description: "General health reminders and account updates",
        importance: 4,
        visibility: 1,
        lights: true,
        vibration: true,
        sound: "qureo_alarm",
      });
    }

    let permission = await PushNotifications.checkPermissions();

    if (permission.receive !== "granted") {
      permission = await PushNotifications.requestPermissions();
    }

    if (permission.receive !== "granted") {
      return { ok: false, reason: "Permission denied" };
    }

    attachNativePushListeners(userId);
    await PushNotifications.register();
    return { ok: true, reason: "Native push registration requested" };
  } catch (err) {
    console.error("Native push setup failed:", err);
    return { ok: false, reason: err?.message || "Native push setup failed" };
  }
};

// 🔹 Request permission and get token
export const requestForToken = async (userId) => {
  if (Capacitor.isNativePlatform()) {
    return requestNativePushToken(userId);
  }

  try {
    // Check if messaging is supported and initialized
    if (!messaging) {
      return { ok: false, reason: 'Firebase Messaging is not supported in this environment' };
    }
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return { ok: false, reason: 'Notifications are not supported in this environment' };
    }

    // 🔹 Step 1: Request permission before trying to get token
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }

    if (permission === "granted") {
      // 🔹 Step 2: Retrieve FCM token
      const currentToken = await getToken(messaging, {
        vapidKey:
          "BMJDOUrx1qEyDByU8JyAyJ340FUJ36H8qXLE4HogsqPt9aIja029E6KzFsfk8LolFmOnBR4uBm1V4cAQkiSg4T4",
      });

      if (currentToken) {
        console.log("✅ FCM Token fetched:", currentToken);

        // 🔹 Step 3: Send token + userId to backend
        await api.post("/api/notifications/save-token", {
          token: currentToken,
        });

        console.log("📡 Token successfully sent to backend!");
        return { ok: true, token: currentToken };
      } else {
        console.warn(
          "⚠️ No registration token available. Request permission to generate one."
        );
        return { ok: false, reason: 'No registration token available' };
      }
    } else if (permission === "denied") {
      console.warn("🚫 Notifications are blocked by the user.");
      alert(
        "Notifications are currently blocked in your browser. Please enable them in your browser settings to receive alerts."
      );
      return { ok: false, reason: 'Permission denied' };
    } else {
      console.log("Notifications permission dismissed or default.");
      return { ok: false, reason: 'Permission not granted' };
    }
  } catch (err) {
    console.error("❌ Error retrieving or saving FCM token:", err);
    return { ok: false, reason: err?.message || 'Token registration failed' };
  }
};


// 🔹 Handle foreground messages
export const onMessageListener = () =>
  new Promise((resolve) => {
    if (!messaging) {
      console.warn("Firebase Messaging is not supported in this environment.");
      return;
    }
    onMessage(messaging, (payload) => {
      console.log("📩 Received foreground message:", payload);
      resolve(payload);
    });
  });
